import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sidebar, Menu, MenuItem, SubMenu, menuClasses } from 'react-pro-sidebar';
import { Typography, useTheme } from '@mui/material';
import { Icon } from '../icons';
import { Menu as MenuIcon } from '@mui/icons-material';
import { hexToRGBA } from '../../utils/hex-to-rgba';
import { useTranslation } from 'react-i18next';

import { useAuth } from "../../hooks/useAuth";

import './SidebarStyles.css'

const ProjectSidebar = ({ collapsed, setCollapsed }) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth.user);
  const isRoot = user?.is_root;
  const isAdmin = user?.is_admin;
  
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasImage, setHasImage] = useState(false);
  const [toggled, setToggled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  
  const auth = useAuth();

  const handleMenuClick = (path) => {
    navigate(path);
  };

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    auth.logout();
    navigate('/login');
  };

  const sidebarTheme = theme.palette.mode === 'light' ? {
    sidebar: {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
    },
    menu: {
      menuContent: theme.palette.background.default,
      icon: theme.palette.primary.main,
      hover: {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
      },
      disabled: {
        color: theme.palette.action.disabled,
      },
    },
  } : {
    sidebar: {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
    },
    menu: {
      menuContent: theme.palette.background.paper,
      icon: theme.palette.primary.light,
      hover: {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
      },
      disabled: {
        color: theme.palette.action.disabled,
      },
    },
  };

  const menuItemStyles = {
    root: {
      fontSize: '13px',
      fontWeight: 400,
    },
    icon: {
      color: sidebarTheme.menu.icon,
      '& svg': {
        width: '20px',
        height: '20px',
      },
      [`&.${menuClasses.disabled}`]: {
        color: sidebarTheme.menu.disabled.color,
      },
    },
    SubMenuExpandIcon: {
      color: '#b6b7b9',
    },
    // subMenuContent: ({ level }) => ({
    //   backgroundColor:
    //     level === 0
    //       ? hexToRGBA(sidebarTheme.menu.menuContent, hasImage && !collapsed ? 0.4 : 1)
    //       : 'transparent',
    // }),
    button: {
      [`&.${menuClasses.disabled}`]: {
        color: sidebarTheme.menu.disabled.color,
      },
      '&:hover': {
        backgroundColor: hexToRGBA(sidebarTheme.menu.hover.backgroundColor, hasImage ? 0.8 : 1),
        color: sidebarTheme.menu.hover.color,
      },
    },
    label: ({ open }) => ({
      fontWeight: open ? 600 : undefined,
    }),
  };

  if (!user) {
    return null;
  }

  return (
    <Sidebar
      collapsed={collapsed}
      toggled={toggled}
      onBackdropClick={() => setToggled(false)}
      backgroundColor={hexToRGBA(sidebarTheme.sidebar.backgroundColor, hasImage ? 0.9 : 1)}
      rootStyles={{
        height: '100%',
        color: sidebarTheme.sidebar.color,
        width: '240px',
        minWidth: '240px',
        borderRight: `1px solid ${theme.palette.divider}`,
        borderLeft: 'none',
        borderBlock: 'none'
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {isRoot === 1 && (
            <>
              {/* 새로운 "플랫폼 관리" 메뉴 */}
              <div style={{ marginBottom: '15px' }}>
                <div style={{ marginBlock: '8px', marginInline: '30px', display: 'flex', alignItems: 'center' }}>
                  <Typography
                    fontWeight={600}
                    style={{ fontSize: '12px', opacity: 0.7, letterSpacing: '0.5px' }}
                  >
                    {t('sidebars.project.platformManagement')}
                  </Typography>
                </div>
                <Menu menuItemStyles={menuItemStyles}>
                  <MenuItem
                    onClick={() => handleMenuClick('/admin/groups')}
                    style={{
                      height: 'auto',
                      borderRadius: '6px',
                      marginBlock: '2px',
                      marginInline: '10px',
                      paddingBlock: '10px',
                      backgroundColor: location.pathname === '/admin/groups' ? theme.palette.primary.lightOpacity : null,
                    }}
                  >
                    <Icon 
                      name="officeIcon" 
                      style={{ 
                        width: '20px', 
                        height: '20px',
                        color: location.pathname === '/admin/groups' ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                      }} 
                    />
                    <Typography 
                      variant="caption" 
                      style={{ 
                        fontSize: '16px', 
                        fontWeight: 500, 
                        opacity: 0.9, 
                        color: location.pathname === '/admin/groups' ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                      }}>
                      {t('sidebars.project.clients')}
                    </Typography>
                  </MenuItem>

                  <MenuItem
                    onClick={() => handleMenuClick('/platform-logs')}
                    style={{
                      height: 'auto',
                      borderRadius: '6px',
                      marginBlock: '2px',
                      marginInline: '10px',
                      paddingBlock: '10px',
                      backgroundColor: location.pathname === '/platform-logs' ? theme.palette.primary.lightOpacity : null,
                    }}
                  >
                    <Icon 
                      name="usersIcon" 
                      style={{ 
                        width: '20px', 
                        height: '20px',
                        color: location.pathname === '/platform-logs' ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                      }} 
                    />
                    <Typography 
                      variant="caption" 
                      style={{ 
                        fontSize: '16px', 
                        fontWeight: 500, 
                        opacity: 0.9,
                        color: location.pathname === '/platform-logs' ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                      }}>
                      {t('sidebars.project.allUsers')}
                    </Typography>
                  </MenuItem>
                </Menu>
              </div>
            </>
          )}

          {isRoot === 0 && (
            <>
              {/* 기존 "관리 메뉴" */}
              <div style={{ marginBottom: '15px' }}>
                <div style={{ marginBlock: '8px', marginInline: '30px', display: 'flex', alignItems: 'center' }}>
                  <Typography
                    fontWeight={600}
                    style={{ fontSize: '14px', opacity: 0.7, letterSpacing: '0.5px' }}
                  >
                    {t('sidebars.project.adminMenu')}
                  </Typography>
                </div>
                <Menu menuItemStyles={menuItemStyles}>
                  <MenuItem
                    onClick={() => handleMenuClick('/projects')}
                    style={{
                      height: 'auto',
                      borderRadius: '6px',
                      marginBlock: '2px',
                      marginInline: '10px',
                      paddingBlock: '10px',
                      backgroundColor: (location.pathname === '/projects' || location.pathname === '/master') ? theme.palette.primary.lightOpacity : null,
                    }}
                  >
                    <Icon 
                      name="blueprintIcon" 
                      style={{ 
                        width: '20px', 
                        height: '20px',
                        color: (location.pathname === '/projects' || location.pathname === '/master') ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                      }} 
                    />
                    <Typography 
                      variant="caption" 
                      style={{ 
                        fontSize: '16px', 
                        fontWeight: 500, 
                        opacity: 0.9,
                        color: (location.pathname === '/projects' || location.pathname === '/master') ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                      }}>
                      {t('sidebars.project.projectManagement')}
                    </Typography>
                  </MenuItem>
                  
                  {/* 마스터 데이터 서브메뉴로 변경 */}
                  {/* <SubMenu
                    label={
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Icon 
                          name="nodesIcon" 
                          style={{ 
                            width: '20px', 
                            height: '20px',
                            color: location.pathname.startsWith('/master') ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                          }} 
                        />
                        <Typography 
                          variant="caption" 
                          style={{ 
                            fontSize: '16px', 
                            fontWeight: 500, 
                            opacity: 0.9,
                            marginLeft: '10px',
                            color: location.pathname.startsWith('/master') ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                          }}>
                          마스터 데이터
                        </Typography>
                      </div>
                    }
                    style={{
                      height: 'auto',
                      borderRadius: '6px',
                      marginBlock: '2px',
                      marginInline: '10px',
                      paddingBlock: '10px',
                      backgroundColor: location.pathname.startsWith('/master') ? theme.palette.primary.lightOpacity : null,
                    }}
                  >
                    <MenuItem
                      onClick={() => handleMenuClick('/master/machine')}
                      style={{
                        height: 'auto',
                        borderRadius: '6px',
                        marginBlock: '2px',
                        marginInline: '10px',
                        paddingBlock: '10px',
                        // backgroundColor: location.pathname === '/master/machine' ? theme.palette.primary.lightOpacity : null,
                      }}
                    >
                      <Icon 
                        name="machineIcon" 
                        style={{ 
                          width: '18px', 
                          height: '18px',
                          color: location.pathname === '/master/machine' ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                        }} 
                      />
                      <Typography 
                        variant="caption" 
                        style={{ 
                          fontSize: '14px', 
                          fontWeight: 500, 
                          opacity: 0.9,
                          color: location.pathname === '/master/machine' ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                        }}>
                        장비 목록
                      </Typography>
                    </MenuItem>
                    
                    <MenuItem
                      onClick={() => handleMenuClick('/master/worker')}
                      style={{
                        height: 'auto',
                        borderRadius: '6px',
                        marginBlock: '2px',
                        marginInline: '10px',
                        paddingBlock: '10px',
                        // backgroundColor: location.pathname === '/master/worker' ? theme.palette.primary.lightOpacity : null,
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        style={{ 
                          fontSize: '14px', 
                          fontWeight: 500, 
                          opacity: 0.9,
                          color: location.pathname === '/master/worker' ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                        }}>
                        작업자 목록
                      </Typography>
                    </MenuItem>
                  </SubMenu> */}

                  {isAdmin === 1 && (
                    <MenuItem
                      onClick={() => handleMenuClick('/users')}
                      style={{
                        height: 'auto',
                        borderRadius: '6px',
                        marginBlock: '2px',
                        marginInline: '10px',
                        paddingBlock: '10px',
                        backgroundColor: location.pathname === '/users' ? theme.palette.primary.lightOpacity : null,
                      }}
                    >
                      <Icon 
                        name="usersIcon" 
                        style={{ 
                          width: '20px', 
                          height: '20px',
                          color: location.pathname === '/users' ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                        }} 
                      />
                      <Typography 
                        variant="caption" 
                        style={{ 
                          fontSize: '16px', 
                          fontWeight: 500, 
                          opacity: 0.9,
                          color: location.pathname === '/users' ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                        }}>
                        {t('sidebars.project.userManagement')}
                      </Typography>
                    </MenuItem>
                  )}
                </Menu>
              </div>
            </>
          )}
        </div>
      </div>
    </Sidebar>
  );
};

export default ProjectSidebar;